import styles from './EntityPickerActions.module.scss';

import CloseIcon from '@mui/icons-material/Close';
import { Theme, useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { useEventCallback } from '@mui/material/utils';
import { Row } from '@tanstack/react-table';
import { isEqual, noop } from 'lodash';
import { type JSX, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TablePrefilterProvider } from '@work4all/components/lib/components/table/TablePrefilterProvider';
import { TableInitialState } from '@work4all/components/lib/dataDisplay/basic-table/hooks/useTableStateBag';
import { LockSubscription } from '@work4all/components/lib/hooks/object-lock-subscription/LockSubscription';
import {
  IStackItem,
  StackProvider,
} from '@work4all/components/lib/navigation/history-stack';
import { Content } from '@work4all/components/lib/navigation/navigation-overlay/components/content/Content';
import { Heading3 } from '@work4all/components/lib/typography/heading3/Heading3';

import { CustomFieldsConfigProvider } from '@work4all/data/lib/custom-fields';
import { OverlayFullscreenContext } from '@work4all/data/lib/hooks';
import { PrintBagProvider } from '@work4all/data/lib/hooks/useHandlePrint';

import { Filters } from '@work4all/models/lib/DataProvider';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import {
  ListPageContext,
  ListPageContextValue,
} from '../../containers/file-entities-lists/list-page-context';
import { MaskConfig } from '../../containers/mask-overlays/mask-overlay';
import { OpenMaskOverride } from '../../containers/mask-overlays/mask-overlay/hooks/use-open-mask';
import { settings, useSetting } from '../../settings';
import { ArticleTable } from '../data-tables/article-table/ArticleTable';
import { ContractsTable } from '../data-tables/ContractsTable';
import { CustomersTable } from '../data-tables/CustomersTable';
import { EMailSignatureTable } from '../data-tables/EmailSignatureTable';
import { EmailTemplateTable } from '../data-tables/EmailTemplateTable';
import { IEntityTableV8 } from '../data-tables/entity-table/EntityTableV8';
import { InboundDeliveryNotesTable } from '../data-tables/InboundDeliveryNotesTable';
import { LedgerAccountTable } from '../data-tables/ledger-account-table/LedgerAccountTable';
import { OrdersTable } from '../data-tables/OrdersTable';
import { ProjectTable } from '../data-tables/project-table/ProjectTable';
import { ServiceContractTable } from '../data-tables/ServiceContractTable';
import { SuppliersTable } from '../data-tables/SuppliersTable';
import { EntityTableLayoutV8Props } from '../data-tables/table/EntityTableLayoutV8';
import { TextBuildingBlockTable } from '../data-tables/text-building-block-table/TextBuildingBlockTable';
import { TicketsTable } from '../data-tables/TicketsTable';
import { WordDocumentTemplateTable } from '../data-tables/WordDocumentTemplateTable';
import { WordLetterTemplateTable } from '../data-tables/WordLetterTemplateTable';

import { MaskModal } from './MaskModal';

interface PickFromListModalProps<T> {
  open: boolean;
  entity: Entities;
  multiple: boolean;
  onClose: () => void;
  onConfirm: (selected: T[]) => void;
  prefilter?: Filters;
  data?: T;
  overridesTableProps?: Pick<EntityTableLayoutV8Props, 'onRowDoubleClick'>;
}

function getTableComponent(entity: Entities): typeof CustomersTable {
  switch (entity) {
    case Entities.article:
      return ArticleTable;

    case Entities.customer:
      return CustomersTable;

    case Entities.supplier:
      return SuppliersTable;

    case Entities.project:
      return ProjectTable;

    case Entities.wordLetterTemplate:
      return WordLetterTemplateTable;

    case Entities.wordDocumentTemplate:
      return WordDocumentTemplateTable;

    case Entities.eMailTemplate:
      return EmailTemplateTable;

    case Entities.eMailSignature:
      return EMailSignatureTable;

    case Entities.ledgerAccount:
      return LedgerAccountTable;

    case Entities.textBuildingBlock:
      return TextBuildingBlockTable;

    case Entities.inboundDeliveryNote:
      return InboundDeliveryNotesTable;
    case Entities.order:
      return OrdersTable;
    case Entities.ticket:
      return TicketsTable;
    case Entities.contract:
      return ContractsTable;
    case Entities.serviceContract:
      return ServiceContractTable;
    default:
      return null;
  }
}

export function PickFromListModal<T>(props: PickFromListModalProps<T>) {
  const {
    open,
    entity: entityType,
    multiple,
    prefilter,
    data,
    overridesTableProps,
  } = props;

  const onClose = useEventCallback(props.onClose);
  const onConfirm = useEventCallback(props.onConfirm);

  const [initialView, setInitialView] = useState<IStackItem | null>(null);
  const [maskConfig, setMaskConfig] = useState<{
    open: boolean;
    config: MaskConfig;
  }>({
    open: false,
    config: {
      entity: null,
      id: null,
    },
  });

  useEffect(() => {
    if (open) {
      const TableComponent = getTableComponent(entityType);

      if (!TableComponent) {
        setInitialView(null);
        return;
      }

      const onOpenMask = () => {
        return {
          handler: (config) => {
            setMaskConfig({ open: true, config });
          },
        };
      };

      setInitialView({
        view: (
          <OpenMaskOverride value={{ onOpenMask }}>
            <TablePrefilterProvider value={{ prefilter }}>
              <LockSubscription entity={entityType}>
                <TableComponentWrapper
                  entityType={entityType}
                  multiple={multiple}
                  onClose={onClose}
                  onConfirm={onConfirm}
                  renderTable={(renderTableProps) => (
                    <TableComponent
                      prefilter={prefilter}
                      enableMultiRowSelection={multiple}
                      enableURLState={false}
                      {...renderTableProps}
                      {...overridesTableProps}
                    />
                  )}
                />
              </LockSubscription>
            </TablePrefilterProvider>
          </OpenMaskOverride>
        ),
      });
    }
  }, [
    open,
    entityType,
    multiple,
    onClose,
    onConfirm,
    prefilter,
    data,
    overridesTableProps,
  ]);

  const handleExited = () => {
    setInitialView(null);
  };

  const handleMaskClose = () => {
    setMaskConfig((c) => ({ ...c, open: false }));
  };
  return (
    <>
      <OverlayFullscreenContext.Provider
        value={{ fullscreen: true, setFullscreen: noop, setSettingsKey: noop }}
      >
        <StackProvider onClose={onClose} initialView={initialView}>
          {initialView && (
            <CustomFieldsConfigProvider entity={entityType}>
              <Content open={open} close={onClose} onExited={handleExited} />
            </CustomFieldsConfigProvider>
          )}
        </StackProvider>
      </OverlayFullscreenContext.Provider>

      <MaskModal
        open={maskConfig.open}
        onClose={handleMaskClose}
        onAfterSave={handleMaskClose}
        {...maskConfig.config}
      />
    </>
  );
}

function TableComponentWrapper<T>(props: {
  entityType: Entities;
  multiple: boolean;
  onConfirm: (selected: T[]) => void;
  onClose: () => void;
  renderTable: (props: Pick<IEntityTableV8, 'onRowsSelected'>) => JSX.Element;
  initialState?: TableInitialState;
}) {
  const { entityType, multiple, onConfirm, onClose, renderTable } = props;

  const { t } = useTranslation();

  const { value: hideClosedEntitiesSetting } = useSetting(
    settings.hideClosedEntities({ entityType })
  );

  const hideClosedEntitiesSettingRef = useRef(hideClosedEntitiesSetting);

  useEffect(() => {
    hideClosedEntitiesSettingRef.current = hideClosedEntitiesSetting;
  }, [hideClosedEntitiesSetting]);

  const [selected, setSelected] = useState<T[]>([]);

  const isSmUp = useMediaQuery<Theme>((t) => t.breakpoints.up('sm'));

  const handleSelectionChange = useEventCallback((rows: Row<unknown>[]) => {
    // Remove all grouped rows from the selection and map to the original
    // data.
    const selected = rows
      .filter((row) => !row.getIsGrouped())
      .map((row) => row.original as T);

    // Automatically select the clicked row on mobile when using single selection.
    if (!isSmUp && !multiple && selected.length > 0) {
      onConfirm(selected);
    } else {
      setSelected((prev) => {
        if (isEqual(prev, selected)) return prev;
        return selected;
      });
    }
  });

  const listPageContextValue = useMemo<ListPageContextValue | null>(() => {
    if (!entityType) return null;
    return { entityType };
  }, [entityType]);

  return (
    <Stack height="100%">
      <Stack direction="row" justifyContent="space-between" padding={2}>
        <Stack direction="row" alignItems="center">
          <IconButton size="large" onClick={onClose}>
            <CloseIcon />
          </IconButton>
          <Heading3 className={styles['text-02']}>
            {t('COMMON.PICK_FROM', {
              from: t(`COMMON.${entityType.toUpperCase()}`),
            })}
          </Heading3>
        </Stack>

        {selected.length > 0 && (
          <Button
            variant="contained"
            size="large"
            onClick={() => onConfirm(selected)}
          >
            {t('COMMON.APPLY_SELECTION')}
          </Button>
        )}
      </Stack>

      <Box flex={1}>
        <ListPageContext.Provider value={listPageContextValue}>
          <PrintBagProvider>
            {renderTable({ onRowsSelected: handleSelectionChange })}
          </PrintBagProvider>
        </ListPageContext.Provider>
      </Box>
    </Stack>
  );
}
